// src/services/notificationService.js
import { messaging, getToken, onMessage, VAPID_KEY, isMessagingAvailable, areNotificationsSupported } from '@/firebase/config'
import api from '@/services/api'

class NotificationService {
  constructor() {
    this.currentToken = null
    this.serviceWorkerRegistration = null
  }

  /**
   * ✅ Registrar Service Worker
   */
  async registerServiceWorker() {
    if (!('serviceWorker' in navigator)) {
      console.warn('⚠️ Service Workers no soportados en este navegador')
      return null
    }

    try {
      console.log('📝 Registrando Service Worker...')
      
      // Registrar el Service Worker
      // const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js', {
      //   scope: '/'
      // })
      // ✅ Usar ruta relativa al publicPath
      const swPath = process.env.NODE_ENV === 'production' 
        ? '/platform/firebase-messaging-sw.js'  // Producción
        : '/firebase-messaging-sw.js'            // Desarrollo

      const registration = await navigator.serviceWorker.register(swPath, {
        scope: process.env.NODE_ENV === 'production' ? '/platform/' : '/'
      })

      console.log('✅ Service Worker registrado:', registration)
      
      // Esperar a que el SW esté activo
      await navigator.serviceWorker.ready
      
      this.serviceWorkerRegistration = registration
      
      console.log('✅ Service Worker listo y activo')
      
      return registration
    } catch (error) {
      console.error('❌ Error registrando Service Worker:', error)
      return null
    }
  }

  /**
   * ✅ Verificar si todo está configurado correctamente
   */
  async checkSetup() {
    const checks = {
      notificationsSupported: areNotificationsSupported(),
      messagingAvailable: isMessagingAvailable(),
      serviceWorkerRegistered: !!this.serviceWorkerRegistration,
      permission: Notification.permission
    }

    console.log('🔍 Estado de configuración:', checks)
    
    return checks
  }

  /**
   * ✅ Solicitar permiso para notificaciones
   */
  async requestPermission() {
    try {
      // Verificar soporte
      if (!areNotificationsSupported()) {
        console.error('❌ Notificaciones no soportadas')
        return false
      }

      // Registrar Service Worker primero
      if (!this.serviceWorkerRegistration) {
        await this.registerServiceWorker()
      }

      console.log('📱 Solicitando permiso para notificaciones...')
      
      const permission = await Notification.requestPermission()
      
      if (permission === 'granted') {
        console.log('✅ Permiso concedido')
        
        // Esperar un momento para que el SW esté completamente listo
        await new Promise(resolve => setTimeout(resolve, 1000))
        
        await this.registerToken()
        return true
      } else if (permission === 'denied') {
        console.warn('❌ Permiso denegado')
        return false
      } else {
        console.warn('⏸️ Permiso no respondido')
        return false
      }
    } catch (error) {
      console.error('❌ Error solicitando permiso:', error)
      return false
    }
  }

  /**
   * ✅ Obtener y registrar token FCM
   */
  async registerToken() {
    if (!isMessagingAvailable()) {
      console.error('❌ Firebase Messaging no está disponible')
      return null
    }

    try {
      // Asegurar que tenemos el Service Worker registrado
      if (!this.serviceWorkerRegistration) {
        await this.registerServiceWorker()
      }

      console.log('🔑 Obteniendo token FCM...')
      
      // Obtener token FCM con el Service Worker registration
      const token = await getToken(messaging, {
        vapidKey: VAPID_KEY,
        serviceWorkerRegistration: this.serviceWorkerRegistration
      })

      if (token) {
        console.log('✅ Token FCM obtenido:', token.substring(0, 20) + '...')
        this.currentToken = token

        // Registrar token en el backend
        try {
          await api.post('/notification/register-token', {
            fcm_token: token
          })
          console.log('✅ Token registrado en backend')
        } catch (backendError) {
          console.error('❌ Error registrando token en backend:', backendError)
          // No lanzar error, el token se obtuvo correctamente
        }

        return token
      } else {
        console.warn('⚠️ No se pudo obtener token FCM')
        return null
      }
    } catch (error) {
      console.error('❌ Error obteniendo token:', error)
      
      // Errores comunes y sus soluciones
      if (error.code === 'messaging/permission-blocked') {
        console.error('El usuario ha bloqueado las notificaciones')
      } else if (error.code === 'messaging/token-subscribe-failed') {
        console.error('Error suscribiéndose a FCM')
      } else if (error.code === 'messaging/failed-service-worker-registration') {
        console.error('Error con el Service Worker')
      }
      
      return null
    }
  }

  /**
   * ✅ Eliminar token del backend
   */
  async deleteToken() {
    try {
      await api.post('/notification/delete-token')
      this.currentToken = null
      console.log('✅ Token FCM eliminado del backend')
    } catch (error) {
      console.error('❌ Error eliminando token:', error)
    }
  }

  /**
   * ✅ Configurar listener para notificaciones en primer plano
   */
  setupForegroundListener(callback) {
    if (!isMessagingAvailable()) {
      console.error('❌ Firebase Messaging no está disponible')
      return
    }

    console.log('👂 Configurando listener de notificaciones en primer plano...')

    onMessage(messaging, (payload) => {
      console.log('🔔 Notificación recibida en primer plano:', payload)
      
      // Ejecutar callback personalizado
      if (callback && typeof callback === 'function') {
        callback(payload)
      }

      // Mostrar notificación del navegador
      this.showBrowserNotification(payload)
    })

    console.log('✅ Listener configurado')
  }

  /**
   * ✅ Mostrar notificación en el navegador
   */
  showBrowserNotification(payload) {
    if (Notification.permission !== 'granted') {
      console.warn('⚠️ No hay permiso para mostrar notificaciones')
      return
    }

    const title = payload.notification?.title || 'VVMOne'
    const options = {
      body: payload.notification?.body || 'Nueva notificación',
      icon: '/favicon.ico',
      badge: '/favicon.ico',
      data: payload.data,
      tag: payload.data?.notification_id || 'default',
      requireInteraction: false, // Cambiado a false para mejor UX
      vibrate: [200, 100, 200]
    }

    try {
      new Notification(title, options)
      console.log('✅ Notificación mostrada en el navegador')
    } catch (error) {
      console.error('❌ Error mostrando notificación:', error)
    }
  }

  /**
   * ✅ Verificar si hay permiso
   */
  hasPermission() {
    return Notification.permission === 'granted'
  }

  /**
   * ✅ Obtener token actual
   */
  getCurrentToken() {
    return this.currentToken
  }

  /**
   * ✅ Inicializar todo (método conveniente)
   */
  async initialize() {
    try {
      console.log('🚀 Inicializando NotificationService...')
      
      // 1. Registrar Service Worker
      await this.registerServiceWorker()
      
      // 2. Si ya tenemos permiso, obtener token
      if (this.hasPermission()) {
        await this.registerToken()
      }
      
      console.log('✅ NotificationService inicializado')
      return true
    } catch (error) {
      console.error('❌ Error inicializando NotificationService:', error)
      return false
    }
  }
}

// Exportar instancia única (singleton)
export default new NotificationService()